import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { ChequeBookService } from '../../services/cheque.service';
import { PERMISSIONS } from '../../constants/permissions';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { ChequeBook } from '../../interfaces/bank/cheque.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const ChequeBooks: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [books, setBooks] = useState<ChequeBook[]>([]);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bankAccountId, setBankAccountId] = useState('');
  const [bookLabel, setBookLabel] = useState('');
  const [leafFrom, setLeafFrom] = useState('');
  const [leafTo, setLeafTo] = useState('');
  const [numberWidth, setNumberWidth] = useState('7');
  const [receivedDate, setReceivedDate] = useState(toLocalIsoDate());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setBooks(await ChequeBookService.list(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load cheque books');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    BankAccountService.listByInstitution(institutionId).then(setBanks)
      .catch(e => toast.showApiError(e, 'Failed to load bank accounts'));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bankName = (id: string) => {
    const b = banks.find(x => x.id === id);
    return b ? `${b.bankName} ${b.accountNumber}` : '—';
  };
  const valid = !!bankAccountId && bookLabel.trim() && Number(leafFrom) > 0 && Number(leafTo) >= Number(leafFrom);

  const save = async () => {
    try {
      setSaving(true);
      await ChequeBookService.create({
        institutionId, bankAccountId, bookLabel: bookLabel.trim(), leafFrom: Number(leafFrom), leafTo: Number(leafTo),
        numberWidth: Number(numberWidth) || undefined, receivedDate,
      });
      toast.showSuccess('Cheque book added');
      setOpen(false);
      setBookLabel(''); setLeafFrom(''); setLeafTo('');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to add cheque book');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b: ChequeBook) => {
    try {
      await ChequeBookService.remove(b.id!);
      toast.showSuccess('Cheque book removed');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to remove cheque book');
    }
  };

  return (
    <Box data-help-page="banking.chequeBooks.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Cheque books</Typography>
        {canManage && <Button data-help="banking.chequeBooks.new" variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add cheque book</Button>}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer data-help-section="banking.chequeBooks.table" component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.chequeBooks.col.book">Book</TableCell><TableCell data-help="banking.chequeBooks.col.bank">Bank account</TableCell><TableCell data-help="banking.chequeBooks.col.leaves">Leaves</TableCell>
                <TableCell data-help="banking.chequeBooks.col.used">Used</TableCell><TableCell data-help="banking.chequeBooks.col.next">Next leaf</TableCell><TableCell data-help="banking.chequeBooks.col.received">Received</TableCell>
                <TableCell data-help="banking.chequeBooks.col.status">Status</TableCell><TableCell data-help="banking.chequeBooks.col.actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {books.map(b => (
                <TableRow key={b.id} hover>
                  <TableCell>{b.bookLabel}</TableCell>
                  <TableCell>{bankName(b.bankAccountId)}</TableCell>
                  <TableCell>{b.leafFrom} - {b.leafTo}</TableCell>
                  <TableCell>{b.usedLeaves ?? 0} / {b.totalLeaves ?? b.leafTo - b.leafFrom + 1}</TableCell>
                  <TableCell>{b.nextLeaf ?? '—'}</TableCell>
                  <TableCell>{b.receivedDate ? formatDate(b.receivedDate) : '—'}</TableCell>
                  <TableCell><Chip size="small" label={b.status ?? 'ACTIVE'} /></TableCell>
                  <TableCell>
                    {canManage && (b.usedLeaves ?? 0) === 0 && <Button data-help="banking.chequeBooks.action.remove" size="small" color="error" onClick={() => remove(b)}>Remove</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {books.length === 0 && <TableRow><TableCell colSpan={8} align="center">No cheque books yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.chequeBook.page' } as any}>
        <DialogTitle>Add cheque book</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.chequeBook.section.book">
          <SearchableSelect<BankAccount> data-help="bank.chequeBook.field.bank" label="Bank account" options={banks} value={bankAccountId} required
            getOptionLabel={b => `${b.bankName} ${b.accountNumber}`} onChange={id => setBankAccountId(id)} />
          <TextField data-help="bank.chequeBook.field.label" label="Book label" value={bookLabel} required fullWidth onChange={e => setBookLabel(e.target.value)} />
          <TextField data-help="bank.chequeBook.field.from" label="First leaf number" type="number" value={leafFrom} required fullWidth onChange={e => setLeafFrom(e.target.value)} />
          <TextField data-help="bank.chequeBook.field.to" label="Last leaf number" type="number" value={leafTo} required fullWidth onChange={e => setLeafTo(e.target.value)} />
          <TextField data-help="bank.chequeBook.field.width" label="Number width (zero padding)" type="number" value={numberWidth} fullWidth onChange={e => setNumberWidth(e.target.value)} />
          <TextField data-help="bank.chequeBook.field.received" label="Received date" type="date" value={receivedDate} fullWidth InputLabelProps={{ shrink: true }}
            onChange={e => setReceivedDate(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button data-help="bank.chequeBook.cancel" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-help="bank.chequeBook.save" variant="contained" disabled={saving || !valid} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChequeBooks;
